// @flow
import type { ComponentType, ElementRef, Node } from 'react';
import React, { Component } from 'react';
import classNames from 'classnames';
import styled, { type StyledComponent } from 'styled-components';

import { DSTokenMap, Spacing } from '@rover/kibble/styles';
import type { Untranslated } from '@rover/shared/js/types';

import { getNextId } from '../../../utils/elementIdCreator';
import Popover from '../../callouts/Popover';
import FormGroup from '../../formFields/FormGroup';
import FormValidationError from '../../formFields/FormValidationError';

export type Props = {
  id?: string,
  name?: string,
  children?: (any) => Node,
  label?: Node,
  subLabel?: Node,
  subLabelPosition?: 'above' | 'below' | 'right',
  placeholder?: Untranslated,
  className?: string,
  validationError?: ?Untranslated,
  validationErrorPlacement?: Popover.Placement,
  style?: any,
  inputRef?: (ElementRef<any>) => void,
  InputComponent?: ComponentType<any>,
  useFieldset?: boolean,
  useLabelElementAsCaption?: boolean,
};

export const Label: StyledComponent<{}> = styled.label`
  font-weight: 700;
  color: ${DSTokenMap.TEXT_COLOR_SECONDARY};
  margin-bottom: ${Spacing.XS};
`;

export const SubLabel: StyledComponent<{}> = styled.div`
  line-height: 1.6;
  color: ${DSTokenMap.TEXT_COLOR_TERTIARY};
  margin-bottom: 6px;
`;

const RightLabel: StyledComponent<{}> = styled.div`
  line-height: 1.6;
  color: ${DSTokenMap.TEXT_COLOR_TERTIARY};
  margin-left: ${Spacing.M};
`;

const FlexRow: StyledComponent<{}> = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const getInputId = (name?: string, idProp?: string, componentId: string) =>
  idProp || `${name || ''}-${componentId}`;
const getFieldsetLegendId = (name?: string, idProp?: string, componentId: string) =>
  `form-group-${getInputId(name, idProp, componentId)}`;

export default class LabelAndErrorFormGroup extends Component<Props> {
  static defaultProps = {
    subLabelPosition: 'above',
    useFieldset: false,
    useLabelElementAsCaption: true,
  };

  renderChildrenComponent = (childrenComponent: Node) => {
    const { subLabel, subLabelPosition, validationError, validationErrorPlacement } = this.props;

    if (subLabel && subLabelPosition === 'right') {
      return (
        <FlexRow>
          <FormValidationError message={validationError} placement={validationErrorPlacement}>
            {childrenComponent}
          </FormValidationError>
          <RightLabel>{subLabel}</RightLabel>
        </FlexRow>
      );
    }
    return (
      <FormValidationError message={validationError} placement={validationErrorPlacement}>
        {childrenComponent}
      </FormValidationError>
    );
  };

  id = getNextId();

  render() {
    const {
      id,
      InputComponent,
      className,
      children,
      style,
      label,
      name,
      subLabel,
      subLabelPosition,
      inputRef,
      useFieldset,
      useLabelElementAsCaption,
      placeholder,
      validationError,
      validationErrorPlacement,
      ...other
    } = this.props;
    let labelComponent = null;
    let childrenComponent = null;

    if (label) {
      const labelProps: { [string]: any } = {
        className: 'control-label',
        htmlFor: getInputId(name, id, this.id),
        id: useFieldset ? getFieldsetLegendId(name, id, this.id) : undefined,
        children: label,
      };
      if (useFieldset || !useLabelElementAsCaption) {
        labelProps.as = 'div';
      }
      // eslint-disable-next-line jsx-a11y/label-has-for
      labelComponent = <Label {...labelProps} />;
    }

    const inputComponentProps: { [string]: any } = {
      ...other,
      ref: inputRef,
      name,
      id: getInputId(name, id, this.id),
      placeholder: placeholder || (typeof label === 'string' ? label : ''),
    };
    if (useFieldset && !InputComponent) {
      inputComponentProps['aria-label'] = label || '';
    }

    if (InputComponent) {
      childrenComponent = <InputComponent {...inputComponentProps} />;
    } else if (children) {
      // support function as child
      childrenComponent = children(inputComponentProps);
    }

    const formGroupProps: { [string]: any } = {
      className: classNames({ 'has-error': validationError }, className),
      style,
    };
    if (useFieldset) {
      formGroupProps.as = 'fieldset';
      formGroupProps['aria-labelledby'] = getFieldsetLegendId(name, id, this.id);
    }

    return (
      <FormGroup {...formGroupProps}>
        {labelComponent}
        {subLabel && subLabelPosition === 'above' && <SubLabel>{subLabel}</SubLabel>}
        {this.renderChildrenComponent(childrenComponent)}
        {subLabel && subLabelPosition === 'below' && <SubLabel>{subLabel}</SubLabel>}
      </FormGroup>
    );
  }
}



// WEBPACK FOOTER //
// ../react-lib/src/components/utils/LabelAndErrorFormGroup/LabelAndErrorFormGroup.jsx